import { useMemo } from "react";
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

// Each column may be either:
//   { key, label }                                — falls back to row[key]
//   { key, label, render(row) => ReactNode }      — custom cell rendering
//   { key, label, sortable: false }               — opt OUT of client-side sort
//
// Sorting:
//   * Click a header to sort ascending; click again to sort descending; click a
//     third time to clear the sort (return to server order).
//   * Sorting is client-side over the current page's `data.results` only —
//     it doesn't refetch with `ordering=`. Backed by the same ListTemplate
//     so changes carry across every entity list.
//   * Columns whose render produces React nodes are still sortable when a
//     `sortValue(row)` is supplied; otherwise sort uses the raw row[key].
//
// Selection:
//   * Pass `selectable` (boolean) to show a checkbox column.
//   * `selectedIds` (Set) + `onToggleId(id)` + `onToggleAll(ids[], select)`
//     are controlled by the parent so the selection survives pagination /
//     sort changes.
export default function Table({
  columns,
  data,
  rowActions,
  sortState,           // { key, dir: 'asc'|'desc' } | null
  onSortChange,        // (next | null) => void
  selectable = false,
  selectedIds,         // Set<id>
  onToggleId,          // (id) => void
  onToggleAll,         // (ids, select) => void
}) {
  // Sort the current page client-side. When sortState is null we keep
  // server order.
  const sortedRows = useMemo(() => {
    const rows = data?.results || [];
    if (!sortState?.key) return rows;
    const col = columns.find((c) => c.key === sortState.key);
    if (!col) return rows;
    const valueOf = col.sortValue
      ? col.sortValue
      : (row) => row[col.key];
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      // Null / undefined sort to the end regardless of direction so the
      // "missing data" rows don't crowd the top.
      const an = av === null || av === undefined || av === "";
      const bn = bv === null || bv === undefined || bv === "";
      if (an && bn) return 0;
      if (an) return 1;
      if (bn) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return sortState.dir === "asc" ? av - bv : bv - av;
      }
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
      return sortState.dir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [data, sortState, columns]);

  if (!data?.results?.length) {
    return (
      <div className="rounded-lg border border-border-default bg-bg-primary p-10 text-center text-text-secondary text-sm">
        Nothing to show yet — try adjusting your filters or add a new entry.
      </div>
    );
  }

  const headerClick = (col) => {
    if (col.sortable === false || !onSortChange) return;
    const isActive = sortState?.key === col.key;
    if (!isActive) {
      onSortChange({ key: col.key, dir: "asc" });
    } else if (sortState.dir === "asc") {
      onSortChange({ key: col.key, dir: "desc" });
    } else {
      onSortChange(null);
    }
  };

  const visibleIds = sortedRows.map((r) => r.id);
  const allSelected = selectable && visibleIds.length > 0 && visibleIds.every((id) => selectedIds?.has(id));
  const someSelected = selectable && visibleIds.some((id) => selectedIds?.has(id)) && !allSelected;

  return (
    <div className="overflow-x-auto rounded-lg border border-border-default">
      <table className="w-full text-left text-sm min-w-[640px]">
        <thead className="bg-table-header border-b border-border-default sticky top-0">
          <tr>
            {selectable && (
              <th className="px-3 py-3 w-8">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={(e) => onToggleAll?.(visibleIds, e.target.checked)}
                  className="accent-button-primary cursor-pointer"
                  aria-label={allSelected ? "Deselect all on this page" : "Select all on this page"}
                />
              </th>
            )}
            {columns.map((col) => {
              const isActive = sortState?.key === col.key;
              const sortable = col.sortable !== false && !!onSortChange;
              const SortIcon = !isActive ? ArrowUpDown : sortState.dir === "asc" ? ArrowUp : ArrowDown;
              return (
                <th
                  key={col.key}
                  onClick={sortable ? () => headerClick(col) : undefined}
                  className={`px-3 sm:px-5 py-3 text-left text-[11px] uppercase tracking-wider font-semibold whitespace-nowrap ${
                    sortable
                      ? "cursor-pointer select-none hover:text-text-primary"
                      : ""
                  } ${
                    isActive ? "text-text-primary" : "text-text-secondary"
                  }`}
                  title={sortable ? (isActive ? (sortState.dir === "asc" ? "Sorted ascending — click for desc" : "Sorted descending — click to clear") : "Click to sort") : undefined}
                >
                  <span className="inline-flex items-center gap-1.5">
                    {col.label}
                    {sortable && (
                      <SortIcon className={`w-3 h-3 ${isActive ? "opacity-80" : "opacity-40"}`} />
                    )}
                  </span>
                </th>
              );
            })}
            {rowActions && (
              <th className="px-3 sm:px-5 py-3 text-right text-[11px] uppercase tracking-wider text-text-secondary font-semibold whitespace-nowrap">
                Actions
              </th>
            )}
          </tr>
        </thead>

        <tbody>
          {sortedRows.map((row) => {
            const checked = selectable && !!selectedIds?.has(row.id);
            return (
              <tr
                key={row.id}
                className={`border-b border-border-default hover:bg-bg-secondary/40 transition-colors ${
                  checked ? "bg-amber-500/10" : "bg-table-row"
                }`}
              >
                {selectable && (
                  <td className="px-3 py-3 w-8">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggleId?.(row.id)}
                      className="accent-button-primary cursor-pointer"
                      aria-label={checked ? "Deselect row" : "Select row"}
                    />
                  </td>
                )}
                {columns.map((col) => {
                  const cell = col.render ? col.render(row) : row[col.key];
                  return (
                    <td key={col.key} className="px-3 sm:px-5 py-3 sm:py-3.5 text-text-primary align-top">
                      {cell === null || cell === undefined || cell === "" ? <span className="text-text-secondary">—</span> : cell}
                    </td>
                  );
                })}
                {rowActions && (
                  <td className="px-3 sm:px-5 py-3 sm:py-3.5 whitespace-nowrap">
                    <div className="flex justify-end items-center gap-2 flex-wrap">
                      {rowActions(row)}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
